// ComposedExperienceTerra — Tier-A "terra" skin for composed experiences.
//
// A per-world treatment of the composed runtime (a sibling to Obsidian and
// Aurora), proving the dispatcher pattern generalises onto a WARM/LIGHT,
// organic register. Same data + capture contract as ComposedExperienceSlide
// (both consume useComposedExperience), rendered in the TERRA visual language
// shared with TerraProgressiveBuilder: warm parchment, pressed-paper panels,
// field-notes grain, earthy soil/leaf/honey tones, and — the standout — a
// re-rate rate-slider that grows a small STEM whose size maps to the MAGNITUDE
// of the learner's change of mind. Where Aurora flowed a luminous ribbon,
// Terra grows a sprout: your reading moved, and the plant grew by that much
// (§8.12b: the juice represents the cognition, not the clicks).

import { useEffect, useMemo, useState } from 'react';
import type { Theme } from '../../slideTypes';
import type { ComposedExperienceSlideData } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import type {
  ChoiceBlock,
  CommitBoxBlock,
  CompositionBlock,
  ConfidenceBlock,
  FlagInTextBlock,
  PerspectiveLensBlock,
  RateSliderBlock,
  RevealBlock,
  SelectEvidenceBlock,
  SortZonesBlock,
  StimulusBlock,
} from '../../../../../lib/scenario-builder/composition/types';
import type { Answers, BlockAnswer } from '../../../../../lib/scenario-builder/composition/capture';
import { ExperienceClose } from '../../shared/ExperienceClose';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import {
  useThemeWorldAudio,
  playTerraSprout,
} from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';
import { useComposedExperience } from './useComposedExperience';

interface Props {
  data: ComposedExperienceSlideData;
  theme: Theme;
  isEditing?: boolean;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}

// Terra palette (mockup-faithful constants, matching TerraProgressiveBuilder).
const SOIL = '#5a4326';
const SOIL_SOFT = '#7a5d39';
const LEAF = '#2f6e3b';
const LEAF_DEEP = '#21512b';
const SPROUT = '#7fb83f';
const TERRACOTTA = '#d2613c';
const HONEY = '#e6a82e';
const INK = '#3a2c18';
const PAPER = '#efe6d3';

const STYLE_ID = 'terra-composed-scoped-styles';

// A cue runner bound to the terra engine (from useThemeWorldAudio.playWith).
type PlayWith = ReturnType<typeof useThemeWorldAudio>['playWith'];
// A named-cue runner (leaf-ticks on selection).
type Play = ReturnType<typeof useThemeWorldAudio>['play'];

function scopedCss(): string {
  return `
.tercx { position: relative; --ease-grow: cubic-bezier(.22,1,.36,1); }
.tercx * { box-sizing: border-box; }

.tercx .ter-paper {
  position: relative; border-radius: 22px; padding: 24px 26px;
  background: linear-gradient(180deg, rgba(255,250,236,.94), rgba(243,233,210,.96));
  box-shadow: 0 18px 40px -22px rgba(90,67,38,.5), inset 0 1px 0 rgba(255,255,255,.55);
}
.tercx .ter-paper::before {
  content: ""; position: absolute; inset: 5px; border-radius: 17px;
  border: 1.5px solid ${SOIL_SOFT}; opacity: .32; pointer-events: none;
}
.tercx .ter-kicker { font-size: 0.7rem; letter-spacing: 0.22em; text-transform: uppercase; color: ${TERRACOTTA}; margin-bottom: 8px; font-weight: 700; }
.tercx .ter-prompt { font-family: inherit; font-size: 1.05rem; color: ${INK}; margin: 0 0 6px; line-height: 1.45; }
.tercx .ter-body { color: ${INK}; font-size: 1rem; line-height: 1.62; margin: 0; }
.tercx .ter-quote { border-left: 3px solid ${LEAF}; padding: 6px 0 6px 16px; color: ${SOIL}; font-style: italic; }

.tercx textarea.ter-input {
  width: 100%; font-family: inherit; font-size: 1rem; color: ${INK}; line-height: 1.55;
  background: #fffaf0; border: 1.5px solid rgba(90,67,38,.24); border-radius: 12px; padding: 12px 14px; resize: vertical;
  transition: border-color .3s, box-shadow .3s;
}
.tercx textarea.ter-input:focus { outline: none; border-color: ${LEAF}; box-shadow: 0 0 0 3px rgba(47,110,59,.16); }

/* gauge */
.tercx .ter-scale-val { font-size: 2.6rem; line-height: 1; color: ${LEAF_DEEP}; text-align: center; font-weight: 700; text-shadow: 0 1px 0 rgba(255,255,255,.4); }
.tercx input[type="range"].ter-range { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; border-radius: 999px; outline: none; cursor: grab; border: 1px solid rgba(90,67,38,.2); background: rgba(90,67,38,.16); }
.tercx input[type="range"].ter-range:disabled { cursor: default; }
.tercx input[type="range"].ter-range::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 22px; height: 22px; border-radius: 50% 50% 50% 0; transform: rotate(45deg); background: radial-gradient(circle at 35% 30%, #d6f0b6, ${SPROUT} 55%, ${LEAF}); border: 2px solid #fffaf0; box-shadow: 0 4px 10px -3px rgba(58,44,24,.6); }
.tercx input[type="range"].ter-range::-moz-range-thumb { width: 22px; height: 22px; border-radius: 50% 50% 50% 0; border: 2px solid #fffaf0; background: radial-gradient(circle at 35% 30%, #d6f0b6, ${SPROUT} 55%, ${LEAF}); box-shadow: 0 4px 10px -3px rgba(58,44,24,.6); }

/* the change-of-mind STEM (re-rate standout): a sprout grows by |delta| */
.tercx .ter-track { position: relative; height: 74px; margin: 18px 0 6px; }
.tercx .ter-rail { position: absolute; bottom: 14px; left: 0; right: 0; height: 8px; border-radius: 999px; background: rgba(90,67,38,.16); border: 1px solid rgba(90,67,38,.2); }
.tercx .ter-range.ter-overlay { position: absolute; left: 0; right: 0; bottom: 5px; height: 26px; background: transparent; border: none; z-index: 5; }
.tercx .ter-ghost { position: absolute; bottom: 14px; width: 14px; height: 14px; border-radius: 50% 50% 50% 0; transform: translate(-50%, 50%) rotate(45deg); border: 2px solid ${SOIL_SOFT}; background: #fffaf0; z-index: 3; }
.tercx .ter-stem {
  position: absolute; bottom: 18px; width: 5px; transform: translateX(-50%); transform-origin: bottom center;
  border-radius: 999px 999px 2px 2px; background: linear-gradient(180deg, ${SPROUT}, ${LEAF_DEEP}); z-index: 2;
  transition: left .5s var(--ease-grow), height .55s var(--ease-grow);
}
.tercx .ter-stem::before, .tercx .ter-stem::after {
  content: ""; position: absolute; top: -3px; width: 12px; height: 9px; background: linear-gradient(120deg, #9ccf57, ${LEAF});
}
.tercx .ter-stem::before { left: 3px; border-radius: 0 90% 0 90%; transform: rotate(-8deg); transform-origin: left center; }
.tercx .ter-stem::after { right: 3px; border-radius: 90% 0 90% 0; transform: rotate(8deg); transform-origin: right center; }
.tercx .ter-stem.grown { animation: terSprout .7s var(--ease-grow); }
@keyframes terSprout { 0% { transform: translateX(-50%) scaleY(.86); } 55% { transform: translateX(-50%) scaleY(1.08); } 100% { transform: translateX(-50%) scaleY(1); } }
.tercx .ter-delta { font-size: 0.82rem; letter-spacing: 0.04em; color: ${LEAF_DEEP}; margin-top: 10px; font-weight: 700; }
.tercx .ter-ends { display: flex; justify-content: space-between; font-size: 0.74rem; color: ${SOIL_SOFT}; margin-top: 2px; }

.tercx .ter-btn {
  margin-top: 14px; font-size: 0.92rem; font-weight: 700; letter-spacing: 0.03em; color: #fff7e6; cursor: pointer;
  padding: 12px 26px; border-radius: 999px; border: none; background: linear-gradient(180deg, ${TERRACOTTA}, #b14d2c);
  box-shadow: 0 10px 24px -10px rgba(178,77,44,.8); transition: transform .3s var(--ease-grow), box-shadow .3s, opacity .3s;
}
.tercx .ter-btn:hover:not(:disabled) { transform: translateY(-3px); box-shadow: 0 16px 30px -12px rgba(178,77,44,.85); }
.tercx .ter-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* options */
.tercx .ter-opt {
  width: 100%; text-align: left; color: ${INK}; background: #fffaf0; border: 1.5px solid rgba(90,67,38,.2);
  border-radius: 12px; padding: 12px 14px; font: inherit; cursor: pointer; transition: border-color .25s, box-shadow .25s, background .25s;
}
.tercx .ter-opt:hover:not(:disabled) { border-color: rgba(47,110,59,.55); }
.tercx .ter-opt.ter-on { border-color: ${LEAF}; background: rgba(127,184,63,.16); box-shadow: 0 0 0 2px rgba(47,110,59,.22), inset 0 1px 0 rgba(255,255,255,.5); }
.tercx .ter-opt:disabled { cursor: default; }

/* reveal — a leaf unfurls open once its ref commits */
.tercx .ter-sealed { border: 1.5px dashed rgba(47,110,59,.4); border-radius: 16px; padding: 20px; text-align: center; color: ${SOIL_SOFT}; font-size: 0.86rem; letter-spacing: 0.06em; background: rgba(127,184,63,.08); }
.tercx .ter-reveal { transform-origin: top center; animation: terUnfurl .8s var(--ease-grow) both; overflow: hidden; }
@keyframes terUnfurl { 0% { opacity: 0; transform: perspective(600px) rotateX(-58deg) translateY(-8px); } 100% { opacity: 1; transform: perspective(600px) rotateX(0) translateY(0); } }

/* ending */
.tercx .ter-settle { text-align: center; margin-bottom: 14px; }
.tercx .ter-settle span { font-size: 0.72rem; letter-spacing: 0.26em; text-transform: uppercase; color: ${LEAF_DEEP}; font-weight: 700; }
.tercx .ter-settle .ter-sprig { font-size: 1.9rem; display: block; margin: 6px auto 4px; }
.tercx .ter-settle .ter-arc { height: 2px; width: 120px; margin: 10px auto 0; border-radius: 999px; background: linear-gradient(90deg, transparent, ${SPROUT}, ${TERRACOTTA}, transparent); }

@media (prefers-reduced-motion: reduce) {
  .tercx .ter-stem, .tercx .ter-reveal { transition: none !important; animation-duration: .001s !important; }
  .tercx .ter-stem.grown { animation: none !important; }
}
`;
}

function useScopedStyles(): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss();
      document.head.appendChild(tag);
    }
    tag.dataset.refcount = String(Number(tag.dataset.refcount || '0') + 1);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────

export function ComposedExperienceTerra({
  data,
  theme,
  isEditing,
  onActivityResponse,
  onComplete,
  savedResponse,
}: Props) {
  useScopedStyles();

  const { blocks, answers, setAnswer, complete, values, rollup } = useComposedExperience({
    data,
    isEditing,
    onActivityResponse,
    onComplete,
    savedResponse,
  });

  const { enabled: audioOn, toggle: toggleAudio, play, playWith } = useThemeWorldAudio('terra');

  return (
    <div
      className="tercx h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(1200px 700px at 50% -5%, rgba(230,168,46,0.2), transparent 60%), radial-gradient(900px 600px at 100% 18%, rgba(127,184,63,0.16), transparent 55%), ${PAPER}`,
        color: INK,
        fontFamily: theme.fonts.body,
      }}
    >
      <ThemeWorldAtmosphere worldId="terra" primary={LEAF} accent={TERRACOTTA} background={PAPER} style={{ zIndex: 0 }} />
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 720, padding: '56px 24px 110px' }}>
          <header style={{ marginBottom: 30 }}>
            <div style={{ fontFamily: theme.fonts.code, fontSize: 11, letterSpacing: '0.3em', color: TERRACOTTA, textTransform: 'uppercase', marginBottom: 14 }}>
              🌱 A living reflection
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.title}
                speed={30}
                caretColor={LEAF}
                style={{
                  fontFamily: theme.fonts.heading,
                  fontWeight: 600,
                  fontSize: 'clamp(1.7rem, 4.4vw, 2.7rem)',
                  lineHeight: 1.08,
                  color: LEAF_DEEP,
                  textShadow: `0 1px 0 rgba(255,255,255,0.4)`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.subtitle && <p style={{ color: SOIL, fontSize: 15, margin: '12px 0 0' }}>{data.subtitle}</p>}
          </header>

          <div className="space-y-5">
            {blocks.map((block) => (
              <TerBlock
                key={block.id}
                block={block}
                answers={answers}
                answer={answers[block.id]}
                onAnswer={(a) => setAnswer(block.id, a)}
                play={play}
                playWith={playWith}
              />
            ))}
          </div>

          {complete && data.conclusion && (
            <div className="mt-10">
              <div className="ter-settle">
                <span>The ground holds</span>
                <span className="ter-sprig" aria-hidden>🌿</span>
                <div className="ter-arc" />
              </div>
              <ExperienceClose
                conclusion={data.conclusion}
                theme={theme}
                values={values}
                outcome={data.scoring.mode === 'none' ? undefined : String(Math.round(rollup * 100))}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

interface TerBlockProps {
  block: CompositionBlock;
  answers: Answers;
  answer: BlockAnswer | undefined;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  playWith: PlayWith;
}

function TerBlock({ block, answers, answer, onAnswer, play, playWith }: TerBlockProps) {
  switch (block.type) {
    case 'stimulus':
      return <TerStimulus block={block} />;
    case 'rate-slider':
      return <TerRateSlider block={block} answers={answers} answer={answer} onAnswer={onAnswer} play={play} playWith={playWith} />;
    case 'confidence':
      return <TerRateSlider block={block as unknown as RateSliderBlock} answers={answers} answer={answer} onAnswer={onAnswer} play={play} playWith={playWith} confidence />;
    case 'reveal':
      return <TerReveal block={block} answers={answers} playWith={playWith} />;
    case 'commit-box':
      return <TerCommitBox block={block} answer={answer} onAnswer={onAnswer} playWith={playWith} />;
    case 'choice':
      return <TerChoice block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'select-evidence':
      return <TerSelectEvidence block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'sort-zones':
      return <TerSortZones block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'flag-in-text':
      return <TerFlagInText block={block} answer={answer} onAnswer={onAnswer} play={play} />;
    case 'perspective-lens':
      return <TerPerspective block={block} answer={answer} onAnswer={onAnswer} />;
    default:
      return null;
  }
}

function Paper({ children }: { children: React.ReactNode }) {
  return <div className="ter-paper">{children}</div>;
}

// ── stimulus ──────────────────────────────────────────────────────────────────
function TerStimulus({ block }: { block: StimulusBlock }) {
  return (
    <Paper>
      {block.content.heading && <div className="ter-kicker">{block.content.heading}</div>}
      <p className="ter-body">{block.content.body}</p>
    </Paper>
  );
}

// ── rate-slider (+ confidence) — the change-of-mind STEM standout ──────────────
function TerRateSlider({
  block,
  answers,
  answer,
  onAnswer,
  play,
  playWith,
  confidence,
}: {
  block: RateSliderBlock;
  answers: Answers;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
  playWith: PlayWith;
  confidence?: boolean;
}) {
  const { min, max, step } = block.structure;
  const committed = (answer as { value: number } | undefined)?.value;
  const [pos, setPos] = useState<number>(committed ?? Math.round((min + max) / 2));
  const locked = committed !== undefined;
  const value = locked ? committed : pos;
  const prior = block.ref ? (answers[block.ref] as { value?: number } | undefined)?.value : undefined;
  const pct = (v: number) => ((v - min) / (max - min)) * 100;
  const delta = prior !== undefined ? value - prior : undefined;
  // The stem grows by the MAGNITUDE of the shift, not by clicks: height maps to |delta|.
  const shiftFrac = prior !== undefined ? Math.abs(pct(value) - pct(prior)) / 100 : 0;
  const stemHeight = Math.round(shiftFrac * 52);

  return (
    <Paper>
      <div className="ter-kicker">{confidence ? 'Confidence' : prior !== undefined ? 'Where you land now' : 'Your reading'}</div>
      <p className="ter-prompt">{block.content.prompt}</p>
      <div className="ter-scale-val">{value}</div>
      <div className="ter-track">
        <div className="ter-rail" />
        {prior !== undefined && (
          <span
            className={`ter-stem${locked ? ' grown' : ''}`}
            style={{ left: `${pct(value)}%`, height: `${stemHeight}px` }}
            aria-hidden
          />
        )}
        {prior !== undefined && <span className="ter-ghost" style={{ left: `${pct(prior)}%` }} aria-hidden />}
        <input
          type="range"
          className="ter-range ter-overlay"
          min={min}
          max={max}
          step={step ?? 1}
          value={value}
          disabled={locked}
          onChange={(e) => {
            const v = Number(e.target.value);
            setPos(v);
            play('select');
          }}
        />
      </div>
      <div className="ter-ends">
        <span>{block.content.minLabel ?? min}</span>
        <span>{block.content.maxLabel ?? max}</span>
      </div>
      {prior !== undefined && delta !== undefined && (
        <div className="ter-delta">
          {delta === 0
            ? 'You settled right where you were'
            : delta > 0
              ? `You grew +${delta} — from ${prior} to ${value}`
              : `You settled back ${delta} — from ${prior} to ${value}`}
        </div>
      )}
      {!locked && (
        <button
          className="ter-btn"
          onClick={() => {
            // Step the sprout cue up on a re-rate by the size of the shift.
            const step0 = prior !== undefined ? Math.min(Math.abs(pos - prior), 5) : 0;
            playWith((eng) => playTerraSprout(eng, step0));
            onAnswer({ value: pos });
          }}
        >
          {prior !== undefined ? 'Settle here' : 'Set your reading'}
        </button>
      )}
    </Paper>
  );
}

// ── reveal — a leaf unfurls open once its ref commits ──────────────────────────
function TerReveal({ block, answers, playWith }: { block: RevealBlock; answers: Answers; playWith: PlayWith }) {
  const unlocked = answers[block.ref] !== undefined;
  useEffect(() => {
    if (unlocked) playWith((eng) => playTerraSprout(eng, 1));
  }, [unlocked, playWith]);
  if (!unlocked) {
    return <div className="ter-sealed">🌱 Settle your reading to let the wider view unfurl</div>;
  }
  return (
    <div className="ter-paper ter-reveal">
      <div className="ter-kicker">{block.content.heading || 'The wider view'}</div>
      <p className="ter-body">{block.content.body}</p>
    </div>
  );
}

// ── commit-box — held, then set into a soft quote ─────────────────────────────
function TerCommitBox({
  block,
  answer,
  onAnswer,
  playWith,
}: {
  block: CommitBoxBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  playWith: PlayWith;
}) {
  const committed = (answer as { text: string } | undefined)?.text;
  const [text, setText] = useState(committed ?? '');
  const locked = committed !== undefined;
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const ok = words >= (block.structure.minWords ?? 1);
  return (
    <Paper>
      <div className="ter-kicker">What you'll hold</div>
      <p className="ter-prompt">{block.content.prompt}</p>
      {locked ? (
        <div className="ter-quote">{committed}</div>
      ) : (
        <>
          <textarea className="ter-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={block.content.placeholder} />
          <button
            className="ter-btn"
            disabled={!ok}
            onClick={() => {
              playWith((eng) => playTerraSprout(eng, 0));
              onAnswer({ text: text.trim() });
            }}
          >
            Plant this
          </button>
        </>
      )}
    </Paper>
  );
}

// ── choice ────────────────────────────────────────────────────────────────────
function TerChoice({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: ChoiceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const chosen = (answer as { optionId: string } | undefined)?.optionId;
  const chosenOpt = block.content.options.find((o) => o.id === chosen);
  return (
    <Paper>
      <p className="ter-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.options.map((opt) => (
          <button
            key={opt.id}
            disabled={!!chosen}
            onClick={() => {
              play('select');
              onAnswer({ optionId: opt.id });
            }}
            className={`ter-opt${opt.id === chosen ? ' ter-on' : ''}`}
            style={{ opacity: chosen && opt.id !== chosen ? 0.5 : 1 }}
          >
            {opt.text}
          </button>
        ))}
      </div>
      {chosenOpt?.feedback && <p style={{ marginTop: 12, fontSize: '0.9rem', color: SOIL, fontStyle: 'italic' }}>{chosenOpt.feedback}</p>}
    </Paper>
  );
}

// ── select-evidence — leafy selections ─────────────────────────────────────────
function TerSelectEvidence({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: SelectEvidenceBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { selected: string[] } | undefined)?.selected;
  const [selected, setSelected] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const max = block.structure.maxSelections;
  const atMax = max !== undefined && selected.length >= max;
  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : atMax ? s : [...s, id]));
  return (
    <Paper>
      {block.content.claim && <div className="ter-quote" style={{ marginBottom: 12 }}>{block.content.claim}</div>}
      <p className="ter-prompt">{block.content.prompt}</p>
      <div className="space-y-2">
        {block.content.items.map((it) => (
          <button
            key={it.id}
            disabled={locked || (!selected.includes(it.id) && atMax)}
            onClick={() => {
              play('select');
              toggle(it.id);
            }}
            className={`ter-opt${selected.includes(it.id) ? ' ter-on' : ''}`}
          >
            {it.text}
          </button>
        ))}
      </div>
      {max !== undefined && !locked && <p style={{ marginTop: 8, fontSize: '0.72rem', color: SOIL_SOFT }}>{selected.length}/{max} chosen</p>}
      {!locked && (
        <button className="ter-btn" disabled={selected.length === 0} onClick={() => onAnswer({ selected })}>
          Settle on these
        </button>
      )}
    </Paper>
  );
}

// ── sort-zones ────────────────────────────────────────────────────────────────
function TerSortZones({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: SortZonesBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { placement: Record<string, string> } | undefined)?.placement;
  const [placement, setPlacement] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allPlaced = block.content.items.every((it) => placement[it.id]);
  return (
    <Paper>
      <p className="ter-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.items.map((it) => (
          <div key={it.id}>
            <p style={{ fontSize: '0.92rem', color: INK, margin: '0 0 6px' }}>{it.text}</p>
            <div className="flex flex-wrap gap-2">
              {block.content.zones.map((z) => (
                <button
                  key={z.id}
                  disabled={locked}
                  onClick={() => {
                    play('select');
                    setPlacement((p) => ({ ...p, [it.id]: z.id }));
                  }}
                  className={`ter-opt${placement[it.id] === z.id ? ' ter-on' : ''}`}
                  style={{ width: 'auto', padding: '6px 14px', fontSize: '0.82rem', borderRadius: 999 }}
                >
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      {!locked && (
        <button className="ter-btn" disabled={!allPlaced} onClick={() => onAnswer({ placement })}>
          Settle the sort
        </button>
      )}
    </Paper>
  );
}

// ── flag-in-text ──────────────────────────────────────────────────────────────
function TerFlagInText({
  block,
  answer,
  onAnswer,
  play,
}: {
  block: FlagInTextBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
  play: Play;
}) {
  const committed = (answer as { flagged: string[] } | undefined)?.flagged;
  const [flagged, setFlagged] = useState<string[]>(committed ?? []);
  const locked = committed !== undefined;
  const toggle = (id: string) => setFlagged((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  return (
    <Paper>
      <p className="ter-prompt">{block.content.prompt}</p>
      <p className="ter-body" style={{ lineHeight: 1.9 }}>
        {block.content.segments.map((seg) => {
          const on = flagged.includes(seg.id);
          const wasRight = locked && seg.flagged === true;
          const wasWrong = locked && on && seg.flagged !== true;
          return (
            <span
              key={seg.id}
              onClick={() => {
                if (locked) return;
                play('select');
                toggle(seg.id);
              }}
              style={{
                cursor: locked ? 'default' : 'pointer',
                background: on ? (wasWrong ? 'rgba(210,97,60,0.2)' : 'rgba(127,184,63,0.22)') : 'transparent',
                borderBottom: locked
                  ? `2px solid ${wasRight ? LEAF : wasWrong ? TERRACOTTA : 'transparent'}`
                  : on
                    ? `2px solid ${LEAF}`
                    : '2px solid transparent',
                borderRadius: 3,
                padding: '0 2px',
              }}
            >
              {seg.text}{' '}
            </span>
          );
        })}
      </p>
      {!locked && (
        <button className="ter-btn" disabled={flagged.length === 0} onClick={() => onAnswer({ flagged })}>
          Mark {flagged.length} span{flagged.length === 1 ? '' : 's'}
        </button>
      )}
    </Paper>
  );
}

// ── perspective-lens ──────────────────────────────────────────────────────────
function TerPerspective({
  block,
  answer,
  onAnswer,
}: {
  block: PerspectiveLensBlock;
  answer?: BlockAnswer;
  onAnswer: (a: BlockAnswer) => void;
}) {
  const committed = (answer as { takes: Record<string, string> } | undefined)?.takes;
  const [takes, setTakes] = useState<Record<string, string>>(committed ?? {});
  const locked = committed !== undefined;
  const allFilled = block.content.lenses.every((l) => (takes[l.id] ?? '').trim().length > 0);
  return (
    <Paper>
      <p className="ter-prompt">{block.content.prompt}</p>
      <div className="space-y-3">
        {block.content.lenses.map((lens) => (
          <div key={lens.id}>
            <p style={{ fontSize: '0.82rem', fontWeight: 700, color: LEAF, letterSpacing: '0.06em', margin: '0 0 3px' }}>{lens.label}</p>
            {lens.hint && <p style={{ fontSize: '0.78rem', color: SOIL_SOFT, margin: '0 0 4px' }}>{lens.hint}</p>}
            {locked ? (
              <div className="ter-quote">{takes[lens.id]}</div>
            ) : (
              <textarea
                className="ter-input"
                rows={2}
                value={takes[lens.id] ?? ''}
                onChange={(e) => setTakes((tk) => ({ ...tk, [lens.id]: e.target.value }))}
              />
            )}
          </div>
        ))}
      </div>
      {!locked && (
        <button className="ter-btn" disabled={!allFilled} onClick={() => onAnswer({ takes })}>
          Hold these
        </button>
      )}
    </Paper>
  );
}
